import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  BookOpen,
  Users,
  Handshake,
  Home,
  ShoppingBag,
} from 'lucide-react';

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const menuItems = [
    { to: '/', label: 'Accueil', icon: Home, testId: 'menu-home' },
    { to: '/catalogue', label: 'Formations', icon: BookOpen, testId: 'menu-catalogue' },
    { to: '/ressources', label: 'Ressources', icon: ShoppingBag, testId: 'menu-ressources' },
    { to: '/mon-espace', label: 'Espace apprenant', icon: Users, testId: 'menu-mon-espace' },
    { to: '/partenaire', label: 'Devenir partenaire / formateur', icon: Handshake, testId: 'menu-partenaire' },
  ];

  return (
    <>
      <header className="bg-white shadow-sm border-b border-violet-100 sticky top-0 z-40 backdrop-blur-md bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="cursor-pointer" data-testid="header-logo">
              <h1 className="text-3xl font-bold gradient-text">Insitumed</h1>
              <p className="text-xs text-violet-600 -mt-1">Centre de Formation Professionnelle</p>
            </Link>

            <button
              type="button"
              onClick={() => setOpen(true)}
              className="p-2 rounded-lg text-violet-700 hover:bg-violet-50 transition-colors flex items-center gap-2"
              aria-label="Ouvrir le menu"
              data-testid="hamburger-open"
            >
              <Menu size={28} />
              <span className="hidden sm:inline text-sm font-medium">Menu</span>
            </button>
          </div>
        </div>
      </header>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="menu-backdrop"
        />
      )}

      {/* Slide-out panel */}
      <aside
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="menu-panel"
      >
        <div className="p-6 border-b border-violet-100 flex items-center justify-between bg-gradient-to-r from-violet-50 to-purple-50">
          <div>
            <h2 className="text-xl font-bold gradient-text">Menu</h2>
            <p className="text-xs text-violet-600">Où souhaitez-vous aller ?</p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-2 rounded-lg text-gray-500 hover:bg-white hover:text-violet-600 transition-colors"
            aria-label="Fermer le menu"
            data-testid="hamburger-close"
          >
            <X size={24} />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-md'
                    : 'text-gray-700 hover:bg-violet-50 hover:text-violet-700'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-violet-100 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            © 2025 Insitumed
            <br />
            <a href="mailto:insitumed@myyahoo.com" className="text-violet-600 hover:underline">
              insitumed@myyahoo.com
            </a>
          </p>
        </div>
      </aside>
    </>
  );
}
